import "~/features/quote/components/quote-details/styles/quote-details-items.css";
import "./commodity-handling-unit.css";

import { useQueryClient } from "@tanstack/react-query";
import { Loader } from "@tql/ui-components-react/kendo/indicators";
import { Checkbox } from "@tql/ui-components-react/kendo/inputs";
import { Label } from "@tql/ui-components-react/kendo/labels";
import { Button } from "@tql/ui-components-react/tql/buttons";
import { plusCircle } from "@tql/ui-components-react/untitled/icons";
import {
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";

import { useCreateSavedCommodity } from "~/features/commodities/hooks/use-commodities.ts";
import { HazmatDetailRow } from "~/features/quote/components/shipment-details/components/hazmat-detail-row/hazmat-detail-row.tsx";
import { QuoteDetailsContext } from "~/features/quote/context/quote-details-context.tsx";
import { useQuoteDetailsPageLoadQueries } from "~/features/quote/hooks/use-quote-details-hooks.ts";
import { convertHandlingUnitToCommodityNewBody } from "~/features/quote/utils/commodity-to-handling-unit.ts";
import { useDeploymentType } from "~/shared/hooks/use-deployment-type.ts";
import { useFeatureFlagGate } from "~/shared/hooks/use-feature-flag-gate.ts";
import { useNotifications } from "~/shared/hooks/use-notifications.ts";
import type {
	HandlingUnit,
	OnChange,
	QuoteStateType,
} from "~/shared/types/quote-details-types";

import { CommodityForm } from "./commodity-form.tsx";

type CommodityHandlingUnitProps = {
	handlingUnit: HandlingUnit;
	idx: number;
	updateItem: (
		handlingUnit: HandlingUnit,
		field: keyof HandlingUnit,
		value: OnChange | null,
	) => void;
	onSavingStart?: () => void;
	onSavingEnd?: () => void;
	isSingular?: boolean;
	onRemoveOverride?: (_id: number) => void;
	customerId?: number;
	measuringType?: "LB/IN" | "KG/CM";
	deliveryServices?: QuoteStateType["deliveryServices"];
	onImperialMetricConversion?: (unit: "LB/IN" | "KG/CM", id?: number) => void;
	validationErrors?: Set<string>;
	onClearFieldError?: (fieldKey: string) => void;
	showHazmatDetails?: boolean;
	requireDescription?: boolean;
	hideSaveCommodity?: boolean;
	requireClass?: boolean;
	weightLabel?: string;
	alwaysShowPieceCount?: boolean;
	requirePieceCountForHazmatOnly?: boolean;
	/** When provided (mobile, last item), renders an "Add Another Unit" button inline with Remove/Save. */
	onAddAnotherUnit?: () => void;
	onCancel?: () => void;
	hideRemoveButton?: boolean;
	saveButtonLabel?: string;
	/** Label for the inline "Add Another Unit" button. Defaults to "Handling Unit"
	 *  to match the original desktop Quote Details copy; pass "Add Another Unit"
	 *  (or similar) to opt into the Tender mobile copy without affecting other
	 *  consumers of this shared component. */
	addAnotherUnitLabel?: string;
};

export const CommodityHandlingUnit = ({
	handlingUnit,
	idx,
	updateItem,
	onSavingStart,
	onSavingEnd,
	isSingular,
	onRemoveOverride,
	customerId: propsCustomerId,
	measuringType: propsMeasuringType,
	deliveryServices: propsDeliveryServices,
	onImperialMetricConversion: propsOnImperialMetricConversion,
	validationErrors,
	onClearFieldError,
	showHazmatDetails,
	requireDescription,
	hideSaveCommodity = false,
	requireClass = true,
	weightLabel = "Total Weight",
	alwaysShowPieceCount = false,
	requirePieceCountForHazmatOnly = false,
	onAddAnotherUnit,
	addAnotherUnitLabel = "Handling Unit",
	onCancel,
	hideRemoveButton = false,
	saveButtonLabel = "Save",
}: CommodityHandlingUnitProps) => {
	const { isReady: hazmatFfIsReady, isEnabled: hazmatFfIsEnabled } =
		useFeatureFlagGate("traxLtlHazmatItemization");
	const context = useContext(QuoteDetailsContext);
	const { isInternal } = useDeploymentType();

	const customerId = useMemo(
		() => propsCustomerId ?? context?.quoteState?.customer?.id,
		[propsCustomerId, context?.quoteState?.customer?.id],
	);
	const measuringType = useMemo(
		() => propsMeasuringType ?? context?.quoteState?.measuringType ?? "LB/IN",
		[propsMeasuringType, context?.quoteState?.measuringType],
	);
	const deliveryServices = useMemo(
		() => propsDeliveryServices ?? context?.quoteState?.deliveryServices ?? [],
		[propsDeliveryServices, context?.quoteState?.deliveryServices],
	);
	const onImperialMetricConversion = useMemo(
		() => propsOnImperialMetricConversion ?? context?.imperialMetricConversion,
		[propsOnImperialMetricConversion, context?.imperialMetricConversion],
	);

	const { data } = useQuoteDetailsPageLoadQueries();
	const { mutateAsync: createSavedCommodity } = useCreateSavedCommodity();
	const queryClient = useQueryClient();

	const freightClassData = data?.freightClassData;
	const unitTypes = data?.unitTypes;
	const hazmatClasses = data?.hazmatClasses;
	const hazmatGroups = data?.hazmatGroups;
	const [isSaving, setIsSaving] = useState(false);
	const { showSuccess, showError } = useNotifications();

	const prevSelectedUnit = useRef(measuringType);

	useEffect(() => {
		if (prevSelectedUnit.current !== measuringType) {
			if (onImperialMetricConversion) {
				onImperialMetricConversion(measuringType, handlingUnit.id);
			}
			prevSelectedUnit.current = measuringType;
		}
	}, [handlingUnit.id, onImperialMetricConversion, measuringType]);

	const handleSaveCommodity = useCallback(async () => {
		if (!customerId) {
			showError("Customer ID is required to save commodity", {
				autoClose: false,
			});
			return;
		}

		setIsSaving(true);
		if (onSavingStart) onSavingStart();
		try {
			const commodityData = convertHandlingUnitToCommodityNewBody(
				handlingUnit,
				customerId,
				0,
				measuringType,
			);

			await createSavedCommodity({ ...commodityData, isInternal });

			showSuccess("Successfully saved commodity!");
			if (onSavingEnd) onSavingEnd();
			await queryClient.invalidateQueries({
				queryKey: ["savedCommodities", String(customerId)],
			});
		} catch (error) {
			const message =
				error instanceof Error ? error.message : "Unknown error occurred";
			console.error("Error saving commodity:", error);
			showError(`Failed to save commodity: ${message}`);
		} finally {
			setIsSaving(false);
		}
	}, [
		customerId,
		onSavingStart,
		showError,
		handlingUnit,
		measuringType,
		showSuccess,
		createSavedCommodity,
		isInternal,
		onSavingEnd,
		queryClient,
	]);

	const handleSaveClick = useCallback(() => {
		void handleSaveCommodity();
	}, [handleSaveCommodity]);

	const onChange = useCallback(
		(field: keyof HandlingUnit, value: OnChange | null) => {
			if (onClearFieldError) onClearFieldError(field);
			updateItem(handlingUnit, field, value);
		},
		[handlingUnit, updateItem, onClearFieldError],
	);

	return (
		<>
			<div className={"handling-unit-label"}>Handling Unit {idx + 1}</div>
			<div
				className={"commodity-row"}
				data-testid={"handling-unit"}
				id={`commodity-handling-unit-${handlingUnit.id}`}
			>
				<div className={"commodity-fields-group"}>
					<CommodityForm
						freightClassData={freightClassData}
						groupId={handlingUnit.id}
						isMobile={false}
						measuringType={measuringType}
						mode={"add"}
						onChange={onChange}
						requireClass={requireClass}
						requireDescription={!!requireDescription}
						requirePieceCount={
							requirePieceCountForHazmatOnly ? !!handlingUnit.isHazmat : true
						}
						showCalculatedFields={true}
						showPieceCount={
							alwaysShowPieceCount ||
							deliveryServices.some(
								(service) => service.name === "Sort and Segregate",
							) ||
							(!!showHazmatDetails && !!handlingUnit.isHazmat)
						}
						unitTypes={unitTypes}
						validationErrors={validationErrors}
						values={handlingUnit}
						weightLabel={weightLabel}
					/>
				</div>
				<div className={"hazmat-stackable-vertical"}>
					<div className={"stackable-wrapper"}>
						<Checkbox
							aria-describedby={"stackable"}
							checked={!!handlingUnit.isStackable}
							disabled={false}
							id={`stackable-checkbox-${handlingUnit.id}`}
							name={"stackable"}
							onChange={(e) => {
								onChange("isStackable", e.value);
							}}
							tabIndex={-1}
						/>
						<Label editorId={`stackable-checkbox-${handlingUnit.id}`}>
							Stackable
						</Label>
					</div>
					{handlingUnit && hazmatFfIsEnabled && hazmatFfIsReady ? (
						<div className={"hazmat-wrapper"}>
							<Checkbox
								aria-describedby={"hazmat"}
								checked={!!handlingUnit.isHazmat}
								id={`hazmat-checkbox-${handlingUnit.id}`}
								onChange={(e) => onChange("isHazmat", e.value)}
								tabIndex={-1}
							/>
							<Label editorId={`hazmat-checkbox-${handlingUnit.id}`}>
								Hazmat
							</Label>
						</div>
					) : null}
				</div>
				<div
					className={"handling-unit-action-buttons"}
					data-testid={"handling-unit-actions"}
				>
					{onAddAnotherUnit ? (
						<div
							className={"commodity-action-button handling-unit-add-another"}
						>
							<Button
								onClick={onAddAnotherUnit}
								svgIcon={plusCircle}
								tabIndex={-1}
							>
								{addAnotherUnitLabel}
							</Button>
						</div>
					) : null}
					{hideRemoveButton ? null : (
						<div className={"commodity-action-button"}>
							<Button
								data-testid={`remove-item-${handlingUnit.id}`}
								disabled={isSingular}
								onClick={() => {
									if (onRemoveOverride) {
										onRemoveOverride(handlingUnit.id);
									}
								}}
								tabIndex={-1}
							>
								Remove
							</Button>
						</div>
					)}
					{onCancel ? (
						<div className={"commodity-action-button"}>
							<Button onClick={onCancel} tabIndex={-1} variant={"secondary"}>
								Cancel
							</Button>
						</div>
					) : null}
					{hideSaveCommodity ? null : (
						<div className={"commodity-action-button"}>
							<Button
								onClick={handleSaveClick}
								tabIndex={-1}
								variant={"primary"}
							>
								{isSaving ? <Loader size={"small"} /> : saveButtonLabel}
							</Button>
						</div>
					)}
				</div>
			</div>

			{showHazmatDetails &&
			handlingUnit.isHazmat &&
			hazmatFfIsEnabled &&
			hazmatFfIsReady ? (
				<HazmatDetailRow
					handlingUnit={handlingUnit}
					hazmatClasses={hazmatClasses}
					hazmatGroups={hazmatGroups}
					hideSavedCommoditiesNote={hideSaveCommodity}
					onClearFieldError={
						onClearFieldError
							? (_itemId: number, fieldKey: string) =>
									onClearFieldError(fieldKey)
							: undefined
					}
					validationErrors={validationErrors}
				/>
			) : null}

			<hr className={"tql-divider handling-unit-divider"} />
		</>
	);
};
